/**
 * Plan pricing (§12).
 *
 * One dial per plan: the amount. §12 fixes everything else about a plan — the
 * 7/30/90-day durations, the reply allowance, what the couple plan means — and
 * the API refuses to take any of it from here, because a console that can
 * quietly change what a plan grants is a console that can break the ratio
 * guardrail by accident. So this screen shows duration and reads it back; it
 * only ever writes a price.
 *
 * The price shown is the price charged. §12 is blunt about it: "price inclusive
 * of GST at ₹299, or the checkout surprise kills conversion". Everything here
 * is in whole rupees inclusive of GST, sent to the API as paise, and a saved
 * change reaches the plans screen and checkout within a minute. The
 * confirmation says so in those words, because a moderator reaching for this
 * screen is one step from changing what strangers are billed.
 *
 * Every change lands in the audit log against the operator's handle (§07). A
 * price that moved and nobody remembers who moved it is a support incident.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getPricing, setPlanPrice } from '../api';
import type { PriceUpdateResponse, PricingPlanRow, PricingResponse } from '../api';
import { ErrorState, Skeletons, formatClock } from './kit';

/**
 * Mirrors zPlanPriceUpdate in packages/shared/src/contracts.ts (₹10,000). The
 * API is the authority; this exists so the operator hears about the ceiling
 * before the round trip rather than after it.
 */
const MAX_PAISE = 10_000_00;

/* ------------------------------------------------------------------ *
 * Money
 * ------------------------------------------------------------------ */

/** 29900 → "₹299" · 29950 → "₹299.50". Indian grouping, because the customers are. */
export function formatRupees(paise: number): string {
  const whole = paise % 100 === 0;
  return (paise / 100).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/**
 * "299" → 29900 · "299.5" → 29950 · "₹1,299" → 129900. Null for anything that
 * is not a billable rupee amount. Parsed as text, not as a float, so "0.29"
 * cannot come out as 28 paise.
 */
export function rupeesToPaise(input: string): number | null {
  const s = input.trim().replace(/^₹\s*/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [whole = '0', frac = ''] = s.split('.');
  const paise = Number(whole) * 100 + Number(`${frac}00`.slice(0, 2));
  return Number.isSafeInteger(paise) && paise >= 0 ? paise : null;
}

/** The inverse, for pre-filling the input: 29950 → "299.50", 29900 → "299". */
function paiseToRupeeInput(paise: number): string {
  const whole = Math.trunc(paise / 100);
  const frac = paise % 100;
  return frac === 0 ? String(whole) : `${whole}.${frac < 10 ? `0${frac}` : frac}`;
}

function formatDurationDays(days: number): string {
  if (days <= 0) return '—';
  return days === 1 ? '1 day' : `${days} days`;
}

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

interface SavedChange {
  label: string;
  result: PriceUpdateResponse;
  at: string;
}

export default function Pricing({
  onOverriddenCount,
}: {
  onOverriddenCount: (n: number | null) => void;
}) {
  const [data, setData] = useState<PricingResponse | null>(null);
  const [readAt, setReadAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [lastSaved, setLastSaved] = useState<SavedChange | null>(null);

  // Kept in a ref so the badge callback never becomes a fetch dependency.
  const reportCount = useRef(onOverriddenCount);
  reportCount.current = onOverriddenCount;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPricing();
      setData(res);
      setReadAt(new Date().toISOString());
      setError(null);
      reportCount.current(res.plans.filter((p) => p.overridden).length);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err)));
      reportCount.current(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A save answers with the new amount, but what the screen shows should be
  // what the API will now charge, not what we inferred — so re-read after.
  async function handleSaved(row: PricingPlanRow, result: PriceUpdateResponse) {
    setLastSaved({ label: row.label, result, at: new Date().toISOString() });
    await load();
  }

  if (loading && !data) {
    return (
      <>
        <Intro />
        <Skeletons count={3} height={72} />
      </>
    );
  }

  if (error && !data) {
    // 401/403 never reach here — api.ts drops the console back to the gate on
    // its own. 503 admin_unconfigured and everything else is shown as-is with
    // the API's own words; the code is kept because that is what support asks for.
    return (
      <>
        <Intro />
        <ErrorState
          title={
            error.code === 'admin_unconfigured'
              ? 'The console is switched off on this server'
              : 'Plan pricing did not load'
          }
          message={error.message}
          code={`${error.code}${error.status ? ` · HTTP ${error.status}` : ''}`}
          onRetry={() => void load()}
        />
      </>
    );
  }

  if (!data) return null;

  const overridden = data.plans.filter((p) => p.overridden);
  const paid = data.plans.filter((p) => p.plan !== 'free');

  return (
    <>
      <Intro />

      <div className="summary">
        <div className="summary-cell">
          <span className="figure num">{paid.length}</span>
          <span className="sub">paid plans on sale</span>
        </div>
        {/* An override is a fact, not a fault — neither red nor green. */}
        <div className="summary-cell">
          <span className="figure num">{overridden.length}</span>
          <span className="sub">
            {overridden.length === 1 ? 'priced away from its §12 default' : 'priced away from their §12 defaults'}
          </span>
        </div>
        <div className="summary-cell" style={{ gridColumn: 'span 2', justifyContent: 'center' }}>
          <span className="eyebrow">From the API</span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45 }}>{data.note}</span>
        </div>
      </div>

      {error && (
        <div className="notice is-warn" role="status" style={{ marginBottom: 'var(--s-md)' }}>
          Showing prices as read at {formatClock(readAt)}. The most recent refresh failed: {error.message}
        </div>
      )}

      {lastSaved && (
        <div className="notice is-good" role="status" style={{ marginBottom: 'var(--s-lg)' }}>
          <b>{lastSaved.label}</b> now charges{' '}
          <b className="num">{formatRupees(lastSaved.result.amountPaise)}</b>
          {lastSaved.result.previousPaise !== lastSaved.result.amountPaise && (
            <>
              , was <span className="num">{formatRupees(lastSaved.result.previousPaise)}</span>
            </>
          )}
          . {lastSaved.result.note} Recorded in the audit log against <b>{data.actor}</b> at{' '}
          <span className="num">{formatClock(lastSaved.at)}</span>.
        </div>
      )}

      <div className="price-table-wrap">
        <table className="price-table">
          <thead>
            <tr>
              <th scope="col">Plan</th>
              <th scope="col">Duration</th>
              <th scope="col" className="is-num">
                §12 default
              </th>
              <th scope="col" className="is-num">
                Charged now
              </th>
              <th scope="col">Status</th>
              <th scope="col">
                <span className="sr-only">Change</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.plans.map((row) => (
              <PlanRow key={row.plan} row={row} onSaved={(res) => void handleSaved(row, res)} />
            ))}
          </tbody>
        </table>
      </div>

      <p className="eyebrow" style={{ marginTop: 'var(--s-lg)' }}>
        Read at {formatClock(readAt)} · every change here is written to the audit log with the plan,
        the amount before and after, and your handle (§07)
      </p>
    </>
  );
}

function Intro() {
  return (
    <div className="page-intro">
      <h2 className="eyebrow">Pricing</h2>
      <p>
        What members are charged at checkout, per plan, GST-inclusive (§12). This screen moves the
        amount and nothing else — durations and what a plan grants are fixed in §12 and the API will
        not take them from here. A change is live on the plans screen and at checkout within a
        minute.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Row
 * ------------------------------------------------------------------ */

function PlanRow({
  row,
  onSaved,
}: {
  row: PricingPlanRow;
  onSaved: (res: PriceUpdateResponse) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // Free is free by design (§12: liveness and the basic loop "must never sit
  // behind a paywall"). Pricing it is not a dial this screen offers, whatever
  // the API would accept.
  const priceable = row.plan !== 'free';

  const paise = rupeesToPaise(draft);
  const problem =
    draft.trim().length === 0
      ? null
      : paise === null
        ? 'Whole rupees, or rupees and paise like 299.50. Nothing negative.'
        : paise > MAX_PAISE
          ? `The API caps a plan at ${formatRupees(MAX_PAISE)}.`
          : null;
  const unchanged = paise !== null && paise === row.currentPaise;
  const canSave = paise !== null && problem === null && !unchanged;

  function beginEdit() {
    setDraft(paiseToRupeeInput(row.currentPaise));
    setFailure(null);
    setConfirming(false);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setConfirming(false);
    setFailure(null);
  }

  async function save() {
    if (paise === null) return;
    setBusy(true);
    setFailure(null);
    try {
      const res = await setPlanPrice({ plan: row.plan, amountPaise: paise });
      setEditing(false);
      setConfirming(false);
      onSaved(res);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : String(err));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  const inputId = `price-${row.plan}`;

  return (
    <tr className={row.overridden ? 'is-overridden' : ''}>
      <td>
        <div className="plan-name">{row.label}</div>
        <div className="plan-id">{row.plan}</div>
      </td>
      <td>{formatDurationDays(row.durationDays)}</td>
      <td className="is-num">
        <span className={`price ${row.overridden ? 'is-quiet' : ''}`}>{formatRupees(row.defaultPaise)}</span>
      </td>
      <td className="is-num">
        <span className="price">{priceable ? formatRupees(row.currentPaise) : 'free'}</span>
        {row.overridden && row.currentPaise !== row.defaultPaise && (
          <span className="was" aria-label="default price">
            {formatRupees(row.defaultPaise)}
          </span>
        )}
      </td>
      <td>
        {!priceable ? (
          <span className="chip">not for sale</span>
        ) : row.overridden ? (
          <span className="chip chip-brass" title="A price set from this console is in force">
            overridden
          </span>
        ) : (
          <span className="chip chip-river">§12 default</span>
        )}
      </td>
      <td>
        {!priceable ? null : !editing ? (
          <button type="button" className="btn btn-sm" onClick={beginEdit}>
            Change price
          </button>
        ) : (
          <div className="price-edit">
            {!confirming && (
              <>
                <div className="price-edit-row">
                  <label htmlFor={inputId} className="sr-only">
                    New price for {row.label}, in rupees inclusive of GST
                  </label>
                  <span className="rupee" aria-hidden="true">
                    ₹
                  </span>
                  <input
                    id={inputId}
                    className="input"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canSave) setConfirming(true);
                      if (e.key === 'Escape') cancel();
                    }}
                    disabled={busy}
                    aria-invalid={problem !== null}
                    aria-describedby={`${inputId}-hint`}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setConfirming(true)}
                    disabled={busy || !canSave}
                  >
                    Save
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={cancel} disabled={busy}>
                    Cancel
                  </button>
                </div>
                <span id={`${inputId}-hint`} className={`hint ${problem ? 'is-bad' : ''}`}>
                  {problem ??
                    (unchanged
                      ? 'That is the price already.'
                      : paise !== null
                        ? `${formatRupees(paise)} incl. GST · ${paise.toLocaleString('en-IN')} paise`
                        : 'Rupees, inclusive of GST.')}
                  {row.currentPaise !== row.defaultPaise && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ minHeight: 0, padding: 0, fontSize: 11 }}
                        onClick={() => setDraft(paiseToRupeeInput(row.defaultPaise))}
                        disabled={busy}
                      >
                        use the §12 default, {formatRupees(row.defaultPaise)}
                      </button>
                    </>
                  )}
                </span>
              </>
            )}

            {confirming && paise !== null && (
              <div className="confirm" role="alertdialog" aria-label="Confirm price change">
                <span>
                  Charge <b className="num">{formatRupees(paise)}</b> for {row.label} instead of{' '}
                  <span className="num">{formatRupees(row.currentPaise)}</span>? Members will see
                  this on the plans screen and pay it at checkout within a minute.
                </span>
                <div className="row">
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => void save()} disabled={busy}>
                    {busy ? 'Saving…' : `Yes, charge ${formatRupees(paise)}`}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setConfirming(false)}
                    disabled={busy}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {failure && (
              <div className="notice is-bad" role="alert">
                {failure}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
